import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Search,
  UsersRound,
  Shield,
  Check,
  Trash2,
  AlertCircle,
  Plus,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import {
  getSafetyTemplateAssignedProjects,
  assignSafetyTemplateProjects,
  unassignSafetyTemplateProjects,
  bulkAssignSafetyTemplateProjects,
  bulkUnassignSafetyTemplateProjects,
  listSafetyProjectReportConfigs,
} from "../../../api";
import SafetyProjectReportConfigModal from "./SafetyProjectReportConfigModal";

export default function SafetyProjectAssignmentModal({
  isOpen,
  onClose,
  template = null,
  templates = [],
  projects = [],
  orgId = null,
  currentProjectId = null,
  onSuccess,
}) {
  const isBulk = Array.isArray(templates) && templates.length > 0;
  const singleTemplate = !isBulk ? template : null;

  const [activeTab, setActiveTab] = useState("assign"); // "assign" | "manage"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [projectConfigs, setProjectConfigs] = useState({});
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configModalTargetProject, setConfigModalTargetProject] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch project report configs
  const fetchProjectConfigs = async () => {
    try {
      const res = await listSafetyProjectReportConfigs({ org_id: orgId });
      const list = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      const map = {};
      list.forEach((c) => {
        map[c.project_id] = c;
      });
      setProjectConfigs(map);
    } catch (err) {
      console.warn("Failed to fetch project report configs in assignment modal", err);
    }
  };

  // Fetch assigned projects for single template
  const fetchAssignedProjects = async () => {
    if (!singleTemplate?.id) return;
    setLoadingAssigned(true);
    setError(null);
    try {
      const res = await getSafetyTemplateAssignedProjects(singleTemplate.id);
      const data = res?.data ?? res;
      setAssignedProjects(data?.assigned_projects || []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to fetch assigned projects.";
      setError(msg);
    } finally {
      setLoadingAssigned(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedProjectIds(new Set());
      setError(null);
      setSuccessMessage(null);
      setSearchQuery("");
      setActiveTab("assign");
      fetchProjectConfigs();
      if (singleTemplate?.id) {
        fetchAssignedProjects();
      }
    }
  }, [isOpen, singleTemplate?.id, isBulk]);

  // Set of currently assigned project IDs for single template
  const currentlyAssignedIds = useMemo(() => {
    if (isBulk) return new Set();
    const set = new Set();
    (assignedProjects || []).forEach((a) => {
      if (a.is_active) {
        set.add(Number(a.project_id));
      }
    });
    return set;
  }, [assignedProjects, isBulk]);

  // Owner project ID
  const ownerProjectId = useMemo(() => {
    if (singleTemplate) {
      return Number(singleTemplate.project_id || singleTemplate.source_project_id);
    }
    return currentProjectId ? Number(currentProjectId) : null;
  }, [singleTemplate, currentProjectId]);

  // Filtered available projects for assignment
  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (projects || []).filter((p) => {
      const id = p.id ?? p.project_id;
      const name = p.name ?? p.project_name ?? p.project_title ?? `Project #${id}`;
      if (q && !String(name).toLowerCase().includes(q) && !String(id).includes(q)) {
        return false;
      }
      return true;
    });
  }, [projects, searchQuery]);

  const handleToggleProject = (projectId) => {
    const next = new Set(selectedProjectIds);
    if (next.has(projectId)) {
      next.delete(projectId);
    } else {
      next.add(projectId);
    }
    setSelectedProjectIds(next);
  };

  const handleSelectAll = () => {
    const next = new Set(selectedProjectIds);
    filteredProjects.forEach((p) => {
      const pid = Number(p.id ?? p.project_id);
      if (pid !== ownerProjectId) {
        next.add(pid);
      }
    });
    setSelectedProjectIds(next);
  };

  const handleClearAll = () => {
    setSelectedProjectIds(new Set());
  };

  const handleAssignSubmit = async () => {
    const projectIdsList = Array.from(selectedProjectIds);
    if (projectIdsList.length === 0) {
      setError("Please select at least one project to assign.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (isBulk) {
        const payload = {
          org_id: orgId || (templates[0]?.org_id ?? 1),
          template_ids: templates.map((t) => t.id),
          project_ids: projectIdsList,
        };
        const res = await bulkAssignSafetyTemplateProjects(payload);
        const data = res?.data ?? res;
        setSuccessMessage(
          data?.detail ||
            `Successfully assigned ${templates.length} template(s) to ${projectIdsList.length} project(s).`
        );
      } else {
        const payload = {
          project_ids: projectIdsList,
          current_project_id: ownerProjectId,
        };
        const res = await assignSafetyTemplateProjects(singleTemplate.id, payload);
        const data = res?.data ?? res;
        setSuccessMessage(
          data?.detail || `Successfully assigned template to ${projectIdsList.length} project(s).`
        );
        fetchAssignedProjects();
      }

      setSelectedProjectIds(new Set());
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to assign project(s).";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignProject = async (projectId) => {
    if (!singleTemplate?.id) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        project_ids: [projectId],
        current_project_id: ownerProjectId,
      };
      await unassignSafetyTemplateProjects(singleTemplate.id, payload);
      setSuccessMessage("Project access removed successfully.");
      fetchAssignedProjects();
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to unassign project.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <UsersRound className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isBulk
                  ? `Assign ${templates.length} Templates to Projects`
                  : "Assign Template to Projects"}
              </h2>
              <p className="text-xs text-muted-foreground">
                {isBulk
                  ? `${templates.length} safety templates selected for sharing`
                  : `${singleTemplate?.title || "Template"} (${
                      singleTemplate?.report_header_meta?.format_no ||
                      singleTemplate?.template_code ||
                      `#${singleTemplate?.id}`
                    })`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Header (Single Template Only) */}
        {!isBulk && (
          <div className="flex border-b border-border px-6 pt-2 gap-4 bg-muted/10">
            <button
              onClick={() => setActiveTab("assign")}
              className={`pb-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === "assign"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Assign Projects
            </button>
            <button
              onClick={() => setActiveTab("manage")}
              className={`pb-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-1.5 ${
                activeTab === "manage"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>Current Assignments</span>
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                {currentlyAssignedIds.size}
              </span>
            </button>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-sm text-emerald-600 dark:text-emerald-400">
            <Check className="h-4 w-4 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-4">
          {activeTab === "assign" ? (
            <>
              {/* Search & Quick Controls */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search projects by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                >
                  Clear
                </button>
              </div>

              {/* Projects List */}
              <div className="border border-border rounded-xl divide-y divide-border overflow-hidden max-h-72 overflow-y-auto">
                {filteredProjects.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    No projects found.
                  </div>
                ) : (
                  filteredProjects.map((p) => {
                    const pid = Number(p.id ?? p.project_id);
                    const name =
                      p.name ?? p.project_name ?? p.project_title ?? `Project #${pid}`;
                    const isOwner = pid === ownerProjectId;
                    const isAlreadyAssigned = currentlyAssignedIds.has(pid);
                    const isSelected = selectedProjectIds.has(pid);
                    const isDynamicMode = isBulk || singleTemplate?.report_number_config?.mode === "DYNAMIC_PROJECT";
                    const pCode = projectConfigs[pid]?.project_code;

                    return (
                      <div
                        key={pid}
                        onClick={() => {
                          if (!isOwner) handleToggleProject(pid);
                        }}
                        className={`flex items-center justify-between px-4 py-3 text-sm transition-colors ${
                          isOwner
                            ? "bg-muted/40 opacity-70 cursor-not-allowed"
                            : "hover:bg-accent/40 cursor-pointer"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            disabled={isOwner}
                            checked={isOwner || isSelected || isAlreadyAssigned}
                            onChange={() => {
                              if (!isOwner) handleToggleProject(pid);
                            }}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
                          />
                          <div>
                            <span className="font-medium text-foreground">{name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              #{pid}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isOwner && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-muted text-muted-foreground border border-border">
                              Owner Project
                            </span>
                          )}
                          {!isOwner && isAlreadyAssigned && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                              Already Assigned
                            </span>
                          )}
                          {!isOwner && !isAlreadyAssigned && isSelected && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                              Selected
                            </span>
                          )}

                          {pCode ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono font-bold bg-accent/20 text-foreground border border-border" title={`Project Report Code: ${pCode}`}>
                              {pCode}
                            </span>
                          ) : isDynamicMode ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfigModalTargetProject(pid);
                                setConfigModalOpen(true);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                              title="Project Report Code not configured. Click to set code."
                            >
                              <AlertCircle className="h-3 w-3 shrink-0" />
                              <span>Set Code</span>
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Target projects will receive view and inspection creation rights. Template questions and layout remain safely centralized in the owner project.
              </p>
            </>
          ) : (
            <>
              {/* Current Assignments Tab */}
              {loadingAssigned ? (
                <div className="p-8 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm">Loading assigned projects...</span>
                </div>
              ) : assignedProjects.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  This template is currently not assigned to any other projects.
                </div>
              ) : (
                <div className="border border-border rounded-xl divide-y divide-border overflow-hidden max-h-72 overflow-y-auto">
                  {assignedProjects.map((a) => {
                    const pid = Number(a.project_id);
                    const pObj = (projects || []).find(
                      (p) => Number(p.id ?? p.project_id) === pid
                    );
                    const name =
                      pObj?.name ??
                      pObj?.project_name ??
                      pObj?.project_title ??
                      `Project #${pid}`;

                    return (
                      <div
                        key={a.id || pid}
                        className="flex items-center justify-between px-4 py-3 text-sm hover:bg-accent/20 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Shield className="h-4 w-4 text-primary" />
                          <div>
                            <span className="font-medium text-foreground">{name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              #{pid}
                            </span>
                            {a.assigned_at && (
                              <p className="text-xs text-muted-foreground">
                                Assigned: {new Date(a.assigned_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => handleUnassignProject(pid)}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 rounded-lg border border-destructive/20 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remove Access
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4 bg-muted/20">
          <div className="text-xs text-muted-foreground">
            {activeTab === "assign" && selectedProjectIds.size > 0 && (
              <span>{selectedProjectIds.size} project(s) selected</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              Close
            </button>
            {activeTab === "assign" && (
              <button
                type="button"
                disabled={submitting || selectedProjectIds.size === 0}
                onClick={handleAssignSubmit}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-sm font-medium text-primary-foreground shadow hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>
                  {isBulk
                    ? `Assign to ${selectedProjectIds.size} Project(s)`
                    : "Assign Projects"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Embedded Project Report Config Modal */}
      <SafetyProjectReportConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        initialProjectId={configModalTargetProject}
        orgId={orgId}
        onConfigSaved={(saved) => {
          if (saved?.project_id) {
            setProjectConfigs((prev) => ({
              ...prev,
              [saved.project_id]: saved,
            }));
          }
        }}
      />
    </div>
  );
}
